import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { AlertCircle, Loader2 } from 'lucide-react';
import { CustomerCodeMapping } from '../../store/is/customerCodeMappingApi';
import { useGetOrgsQuery } from '../../store/auth/orgapi';
import { useGetMappingsForInputQuery } from '../../store/is/customerCodeMappingApi';
import { isCustomerOrganization } from '../../services/inputMatchingService';
import { CustomerCodes } from '../../types';

interface OrganizationType {
  _id: string;
  name: string;
  desc: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface Organization {
  _id: string;
  name: string;
  type: OrganizationType[];
  address: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface CustomerCodeMappingFormProps {
  inputId: string;
  inputName: string;
  inputType: string;
  initialData?: CustomerCodeMapping | null;
  onSubmit: (data: Partial<CustomerCodeMapping>) => void;
  onCancel: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CustomerCodeMappingForm: React.FC<CustomerCodeMappingFormProps> = ({
  inputId,
  inputName,
  inputType,
  initialData,
  onSubmit,
  onCancel,
  open,
  onOpenChange
}) => {
  const [formData, setFormData] = useState({
    customer_org_id: '',
    customer_codes: {
      input_code: '',
      inhibitor: '',
      crop: '',
      soil: ''
    } as CustomerCodes,
    description: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch organizations and existing mappings
  const { data: orgsResponse, isLoading: orgsLoading, error: orgsError } = useGetOrgsQuery({});
  const { data: existingMappings = [] } = useGetMappingsForInputQuery(inputId);
  
  // Extract orgList from response
  const orgs = orgsResponse?.orgList || [];

  useEffect(() => {
    if (initialData) {
      setFormData({
        customer_org_id: initialData.customer_org_id || '',
        customer_codes: {
          input_code: initialData.codes?.input_code || '',
          inhibitor: initialData.codes?.inhibitor || '',
          crop: initialData.codes?.crop || '',
          soil: initialData.codes?.soil || ''
        },
        description: initialData.description || ''
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.customer_org_id.trim()) {
      newErrors.customer_org_id = 'Customer organization is required';
    }

    if (!formData.customer_codes.input_code.trim()) {
      newErrors.customer_input_code = 'Customer input code is required';
    }

    // Check if there are available organizations
    if (availableOrgs.length === 0 && !orgsLoading && !orgsError) {
      newErrors.customer_org_id = 'No customer organizations available for mapping';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Use customer_codes as-is; remove empty optional fields
    const codes: CustomerCodes = {
      input_code: formData.customer_codes.input_code,
      ...(formData.customer_codes.inhibitor ? { inhibitor: formData.customer_codes.inhibitor } : {}),
      ...(formData.customer_codes.crop ? { crop: formData.customer_codes.crop } : {}),
      ...(formData.customer_codes.soil ? { soil: formData.customer_codes.soil } : {}),
    };

    const submitData: Partial<CustomerCodeMapping> = {
      customer_org_id: formData.customer_org_id,
      input_id: inputId,
      codes,
      description: formData.description || undefined,
    };

    onSubmit(submitData);
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const handleCodesChange = (field: keyof CustomerCodes, value: string) => {
    setFormData(prev => ({ ...prev, customer_codes: { ...prev.customer_codes, [field]: value } }));
    if (errors.customer_input_code && field === 'input_code') {
      setErrors(prev => ({ ...prev, customer_input_code: '' }));
    }
  };

  const handleOrgChange = (value: string) => {
    setFormData(prev => ({ ...prev, customer_org_id: value }));
    if (errors.customer_org_id) {
      setErrors(prev => ({ ...prev, customer_org_id: '' }));
    }
  };

  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, description: value }));
  };

  // Filter organizations by customer type only - trust user to manage mappings
  const availableOrgs = useMemo(() => {
    // Ensure orgs is an array before processing
    if (!orgs || !Array.isArray(orgs) || orgs.length === 0) return [];
    
    // Filter organizations by customer type only
    return orgs.filter((org: Organization) => {
      // Extract type names from the organization type objects
      const typeNames = org.type?.map(type => type.name) || [];
      
      // Check if organization has customer type
      const isCustomer = isCustomerOrganization(typeNames);
      
      // Include all customer organizations - no filtering by existing mappings
      return isCustomer;
    });
  }, [orgs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit Customer Code Mapping' : 'Add Customer Code Mapping'}
          </DialogTitle>
          <DialogDescription>
            Input: {inputName} ({inputType})
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_org_id">Customer Organization *</Label>
              <Select
                value={formData.customer_org_id}
                onValueChange={handleOrgChange}
                disabled={orgsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={orgsLoading ? "Loading organizations..." : "Select customer organization"} />
                </SelectTrigger>
                <SelectContent>
                  {orgsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span>Loading organizations...</span>
                    </div>
                  ) : orgsError ? (
                    <div className="flex items-center justify-center py-4 text-red-600">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <span>Failed to load organizations</span>
                    </div>
                  ) : availableOrgs.length === 0 ? (
                    <div className="flex items-center justify-center py-4 text-gray-500">
                      <span>No available customer organizations</span>
                    </div>
                  ) : (
                    availableOrgs.map((org: Organization) => (
                      <SelectItem key={org._id} value={org._id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{org.name}</span>
                          <span className="text-sm text-gray-500">{org._id}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.customer_org_id && (
                <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.customer_org_id}
                </p>
              )}
              {availableOrgs.length === 0 && !orgsLoading && !orgsError && (
                <p className="text-sm text-amber-600 mt-1">
                  All customer organizations already have mappings for this input
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="customer_input_code">Customer Input Code *</Label>
              <Input
                id="customer_input_code"
                value={formData.customer_codes.input_code}
                onChange={(e) => handleCodesChange('input_code', e.target.value)}
                placeholder="e.g., SLURRY_WWH"
              />
              {errors.customer_input_code && (
                <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.customer_input_code}
                </p>
              )}
            </div>
          </div>

          {inputType === 'nitrogen_stabilized_fertilizer' && (
            <div>
              <Label htmlFor="customer_inhibitor_code">Customer Inhibitor Code</Label>
              <Input
                id="customer_inhibitor_code"
                value={formData.customer_codes.inhibitor || ''}
                onChange={(e) => handleCodesChange('inhibitor', e.target.value)}
                placeholder="e.g., DMPP (optional)"
              />
            </div>
          )}

          {inputType !== 'fuel' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customer_crop_code">Customer Crop Code</Label>
                <Input
                  id="customer_crop_code"
                  value={formData.customer_codes.crop || ''}
                  onChange={(e) => handleCodesChange('crop', e.target.value)}
                  placeholder="e.g., WWH (optional)"
                />
              </div>
              <div>
                <Label htmlFor="customer_soil_code">Customer Soil Code</Label>
                <Input
                  id="customer_soil_code"
                  value={formData.customer_codes.soil || ''}
                  onChange={(e) => handleCodesChange('soil', e.target.value)}
                  placeholder="e.g., SOIL001 (optional)"
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description for this mapping"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={orgsLoading || availableOrgs.length === 0}
            >
              {orgsLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                initialData ? 'Update Mapping' : 'Create Mapping'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
